import { SliceEntityTablePage, EntityTablePage, sliceEntityTablePageOnlyForExtension, EntityTablePageProps, INDEX } from "@crispico/foundation-react/entity_crud/EntityTablePage";
import { EntityTableSimple } from "@crispico/foundation-react/entity_crud/EntityTableSimple";
import { DropdownProps, Label } from "semantic-ui-react";
import { EntityNameFieldEditor, FieldEditorProps, FieldRendererProps, fieldRenderers } from "@crispico/foundation-react/entity_crud/fieldRenderersEditors";
import React, { ReactNode } from "react";
import { addAfterStartupRunnable, entityDescriptors, ID } from '@crispico/foundation-react/entity_crud/entityCrudConstants';
import { PropsFrom, createSliceFoundation, getBaseReducers, getBaseImpures, DispatchersFrom } from "@crispico/foundation-react/reduxHelpers";
import { addEntityDescriptor, EntityDescriptor, FieldDescriptor } from "@crispico/foundation-react/entity_crud/EntityDescriptor";
import { sliceAuditedFieldDetails, AuditedFieldDetails } from "./AuditedFieldDetails/AuditedFieldDetails";
import { AuditFieldsExtendedView } from "./AuditedFieldDetails/AuditFieldsExtendedView";
import { FIELDS_READ, Utils } from "@crispico/foundation-react/utils/Utils";
import { FilterOperators } from "@crispico/foundation-gwt-js";
import { OverrideableElement } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { ActionName, EXTENDED_VIEW, TRUE_VALUES, AUDITABLE_ENTITY_TYPE, AUDITABLE_FIELD_TYPE, AUDIT_ED_NAME, 
    AUDITABLE_FIELD, AUDITABLE_ENTITY, AuditUtils, VERSION, LAST_ENTITY_MAPPING_ID_IN_FILTER } from "./AuditUtils";
import { FieldType, fieldTypeToPrimitiveFieldTypeMapping } from "@crispico/foundation-react/entity_crud/FieldType";
import { SemanticCOLORS } from "semantic-ui-react/dist/commonjs/generic";
import { FieldSelector } from "@crispico/foundation-react/entity_crud/fieldRenderersEditors/SelectorExt";
import { DropdownOption } from "@crispico/foundation-react/components/Dropdown/DropdownExt";
import { getMessageForField } from "@crispico/foundation-react/components/fieldNameContentAssist/FieldNameContentAssist";
import { Severity } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import { EntityDescriptorForServerUtils } from "@crispico/foundation-react/flower/entityDescriptorsForServer/EntityDescriptorForServerUtils";
import { BigState } from "@crispico/foundation-react/AppMeta";

export let auditEntityDescriptor: EntityDescriptor;

function getLastEntityMappingIdInFilter() {
    const lastEntityMappingIdInFilter = window.sessionStorage.getItem(LAST_ENTITY_MAPPING_ID_IN_FILTER);
    return lastEntityMappingIdInFilter ? lastEntityMappingIdInFilter : "0";
}

export const sliceEntityTablePageAudit = createSliceFoundation(class Ext extends SliceEntityTablePage {

    nestedSlices = {
        ...sliceEntityTablePageOnlyForExtension.nestedSlices,
        auditedFieldDetails: sliceAuditedFieldDetails
    }

    initialState = {
        ...sliceEntityTablePageOnlyForExtension.initialState,
        extendedViewForEntity: "",
        extendedViewEntity: null as any
    }

    reducers = {
        ...sliceEntityTablePageOnlyForExtension.reducers, ...getBaseReducers<Ext>(this)
    }

    impures = {
        ...sliceEntityTablePageOnlyForExtension.impures, ...getBaseImpures<Ext>(this),

        addOneToManyModeFiltersSuper: sliceEntityTablePageOnlyForExtension.impures.addOneToManyModeFilters,
        
        addOneToManyModeFilters(filters: Filter[], oneToManyModeField: string, oneToManyModeEntityDescriptor: EntityDescriptor, oneToManyModeCachedId: any) {
            this.addOneToManyModeFiltersSuper(filters, oneToManyModeField, oneToManyModeEntityDescriptor, oneToManyModeCachedId);
            filters.push(AuditUtils.getFilterForAuditableEntity(oneToManyModeEntityDescriptor.name));
        },
        
        adjustEntities(entities: any[]): any[] {
            let newEntities = [];

            this.addIndexOnEntity(entities);

            for (const result of entities) {
                let entity = { ...result };
                
                if ([ActionName.ADD, ActionName.DELETE].includes(entity.action)) {
                    entity = this.getDispatchers().auditedFieldDetails.processAuditInsertedDeletedValue(entity);
                    newEntities.push(entity);
                } else if (entity.action === ActionName.COMBINE) {
                    const oldValue = JSON.parse(entity.oldValue);
                    const newValue = JSON.parse(entity.newValue);
                    
                    entity.auditableField = [];
                    entity.oldValue = [];
                    entity.newValue = [];

                    Object.keys(newValue).forEach(key => {
                        entity.auditableField.push(key);
                        entity.oldValue.push(oldValue[key]);
                        entity.newValue.push(newValue[key]);
                    });

                    newEntities.push(entity);
                } else {
                    //if already exists an entity with same primary information then we will update it
                    const existingEntity = newEntities.find(x => x.date === entity.date && x.auditableEntity === entity.auditableEntity && x.entityId === entity.entityId && x.action === entity.action)
                    
                    if (existingEntity) {
                        existingEntity.auditableField.push(entity.auditableField);
                        existingEntity.oldValue.push(entity.oldValue);
                        existingEntity.newValue.push(entity.newValue);

                        existingEntity[INDEX] = entity[INDEX];
                    } else {
                        entity.auditableField = [ entity.auditableField ];
                        entity.oldValue = [ entity.oldValue ];
                        entity.newValue = [ entity.newValue ];

                        newEntities.push(entity);
                    }
                }
            }
            return newEntities;
        },
        getFieldsToRequest(fieldsToRequest: string[]) {
            return this.getSlice().entityDescriptor.getGraphQlFieldsToRequest(fieldsToRequest);
        }
    }

    getId(entity: any) {
        return entity.auditableEntity + "-" + entity.index;
    }
});

class AuditEntityDescriptor extends EntityDescriptor {
    getGraphQlFieldsToRequest(fields: string[]) {
        return "index auditableEntity entityId date username sourceCode auditableField oldValue newValue action version";
    }
}

class AuditableEntitySelector extends EntityNameFieldEditor {
    handleChange(event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) {
        this.props.formikProps.setFieldValue(this.props.fieldDescriptor.name, EntityDescriptorForServerUtils.getEntityId(String(data.value)));
    }
}

class AuditableFieldSelector extends FieldSelector {
    protected getEntityName() {
        return EntityDescriptorForServerUtils.getEntityDescriptor(getLastEntityMappingIdInFilter())!.name;
    }

    protected convertField(field: string) {
        return String(EntityDescriptorForServerUtils.getFieldId(getLastEntityMappingIdInFilter(), field));
    }

    protected getOptions() {
        const entityName = this.getEntityName();
        const fields = entityDescriptors[entityName].getAuthorizedFields(FIELDS_READ);
        return Object.keys(fields).filter(field => this.convertField(field)).map(field => { return { value: this.convertField(field), label: fields[field].getLabel() } });
    }

    protected getDropdownOptionFromItem(field: any, entityName: string): DropdownOption {
        return { value: this.convertField(field), label: getMessageForField(field, entityName) }
    }

    protected getItemsFromValue(value: any): any[] {
        return value ? value.split(',').map((fieldId: string) => {
            const fd = EntityDescriptorForServerUtils.getFieldDescriptor(getLastEntityMappingIdInFilter(), fieldId);
            return fd ? fd.name : fieldId;
        }) : [];
    }
}

addAfterStartupRunnable(() => {

    const entityFieldDescriptor = new class extends FieldDescriptor {
        constructor() {
            super();
            this.name = AUDITABLE_ENTITY;
            this.type = AUDITABLE_ENTITY_TYPE;
            this.sortable = false;
        }

        getFieldValue(values: any) {
            return EntityDescriptorForServerUtils.getEntityDescriptor(super.getFieldValue(values))?.name;
        }

        protected renderFieldInternal(RendererClass: any, props: FieldRendererProps, entity: any): ReactNode {
            props.value = entityDescriptors[props.value].getLabel();
            return super.renderFieldInternal(RendererClass, props, entity);
        }

        protected renderFieldEditorInternal(EditorClass: any, props: FieldEditorProps) {
            return React.createElement(AuditableEntitySelector as any, props);
        }
    }();

    const actionFieldDescriptor = new class extends FieldDescriptor {
        constructor() {
            super();
            this.name = "action";
            this.type = FieldType.dropdown;
        }

        protected renderFieldInternal(RendererClass: any, props: FieldRendererProps, entity: any): ReactNode {
            const key = `actionColumn-${props.entity.index}-${props.entity.auditableEntity}`;
            const color = this.fieldDescriptorSettings?.fieldIntervals?.find(x => x.from === props.entity.action)?.color;
            const semanticColor = color != null && color != undefined ? color as SemanticCOLORS : undefined;
            return <span key={key} className={key}><Label basic horizontal color={semanticColor}>{props.entity.action}</Label></span>
        }
    }();

    const fieldsFieldDescriptor = new class extends FieldDescriptor {
        dispatchers!: DispatchersFrom<typeof sliceEntityTablePageAudit>;

        constructor() {
            super();
            this.name = AUDITABLE_FIELD;
            this.type = AUDITABLE_FIELD_TYPE;
            this.sortable = false;
            this.noComposedFields = true;
            //this.allowMultiple = false;
        }

        handleFieldClick = (entity: any, fieldName: string) => (e: React.MouseEvent<HTMLSpanElement>) => {
            e.stopPropagation();
            e.preventDefault();
            this.dispatchers.auditedFieldDetails.setInReduxState({ openForSelector: e.currentTarget.className.split(" ")[0], entity, fieldName, secondaryPopupValues: undefined })
        }

        handleExtendedViewClick = (props: FieldRendererProps) => (e: React.MouseEvent<HTMLSpanElement>) => {
            e.stopPropagation();
            e.preventDefault();
            this.dispatchers.setInReduxState({ extendedViewEntity: this.getAuditFields(props, EXTENDED_VIEW), extendedViewForEntity: e.currentTarget.className.split(" ")[0] })
        }

        protected getAuditFields(props: FieldRendererProps, additionalKey: string) {
            return (
                <span className="AuditTablePage_fieldsColumn_displayFlex">{
                    (props.entity.auditableField as number[]).map((fieldId, fieldPosition) => {
                        const fieldDescriptor = EntityDescriptorForServerUtils.getFieldDescriptor(props.entity.auditableEntity, fieldId);
                        const fieldName = fieldDescriptor ? fieldDescriptor.name : EntityDescriptorForServerUtils.getFieldName(props.entity.auditableEntity, fieldId); 
                        const fieldType = fieldDescriptor ? fieldDescriptor.type : FieldType.defaultScalar;

                        if ([ID, VERSION].includes(fieldName)) {
                            return null;
                        }

                        let innerEntityDescriptor;
                        if (!fieldRenderers[fieldType]) {
                            innerEntityDescriptor = entityDescriptors[fieldType];
                        }

                        let value = props.entity.action === ActionName.DELETE ? props.entity.oldValue[fieldPosition] : props.entity.newValue[fieldPosition];

                        if (!value && [ActionName.ADD, ActionName.DELETE].includes(props.entity.action)) {
                            return null;
                        }

                        if (fieldType === FieldType.boolean) {
                            value = TRUE_VALUES.includes(value);
                        }

                        const className = `${additionalKey}fieldColumn-${props.entity.index}-${props.entity.auditableEntity}-${fieldName}-${fieldPosition}`;

                        return <span key={className} className={`${className} AuditTablePage_fieldsColumn_paddingBetweenValues`} onClick={this.handleFieldClick(props.entity, fieldName)}>
                            <a href="/#" className="OldSchoolLink">{fieldDescriptor ? fieldDescriptor.getLabel() : fieldName}: {(innerEntityDescriptor ? innerEntityDescriptor.name + "/" : "") + value}</a>
                        </span>
                    })
                }</span>
            )
        }

        protected renderFieldInternal(RendererClass: any, props: FieldRendererProps, entity: any): ReactNode {
            const key = EXTENDED_VIEW + props.entity.auditableEntity + '-' + props.entity.index
            return <>
                <span key={key} className={key} style={{float: "right"}} onClick={this.handleExtendedViewClick(props)}>
                    <a href="/#">...</a>
                </span>
                {this.getAuditFields(props, "")}
            </>
        }

        protected renderFieldEditorInternal(EditorClass: any, props: FieldEditorProps) {
            return React.createElement(AuditableFieldSelector as any, props);
        }
    }();

    fieldTypeToPrimitiveFieldTypeMapping[AUDITABLE_ENTITY_TYPE] = FieldType.number;
    fieldTypeToPrimitiveFieldTypeMapping[AUDITABLE_FIELD_TYPE] = FieldType.number;

    auditEntityDescriptor = addEntityDescriptor(new AuditEntityDescriptor({
        name: AUDIT_ED_NAME,
        icon: "database",
        miniFields: [AUDITABLE_ENTITY, "index"],
        defaultSort: { field: "date", direction: "DESC" },
        defaultFilter: { field: "date", operator: FilterOperators.forDate.today.value, enabled: true }
    }, false)
        .addFieldDescriptor({ name: "index", type: FieldType.number })
        .addFieldDescriptor(entityFieldDescriptor)
        .addFieldDescriptor({ name: "entityId", type: FieldType.number })
        .addFieldDescriptor({ name: "username", type: FieldType.string })
        .addFieldDescriptor({ name: "date", type: FieldType.date, format: Utils.dateTimeWithSecFormat })
        .addFieldDescriptor({ name: "version", type: FieldType.string, sortable: false })
        .addFieldDescriptor(actionFieldDescriptor)
        .addFieldDescriptor(fieldsFieldDescriptor)
        .isInDefaultColumnConfig(false, "index", "version")
    );

    sliceEntityTablePageAudit.setEntityDescriptor(auditEntityDescriptor);
    auditEntityDescriptor.infoTable.slice = sliceEntityTablePageAudit;

    auditEntityDescriptor.infoTable.wrappedComponentClass = class extends EntityTablePage<EntityTablePageProps & PropsFrom<typeof sliceEntityTablePageAudit>> {

        constructor(props: PropsFrom<typeof sliceEntityTablePageAudit>) {
            super(props);
            fieldsFieldDescriptor.dispatchers = this.props.dispatchers;

            const that = this;
            this.tableSimpleClass = class extends EntityTableSimple {
                openFilterFormWithField(entityDescriptor: EntityDescriptor, field: string, position: boolean | [number, number]) {
                    const entityMappingIds = AuditUtils.entityMappingIdFromFilters(Array.of(that.props.dispatchers.getFilterForLoad()));
                    if (field === AUDITABLE_FIELD && entityMappingIds.length !== 1) {
                        Utils.showGlobalAlert({ message: _msg('Audit.auditableField.notFilterable'), severity: Severity.INFO });
                    } else {
                        window.sessionStorage.setItem(LAST_ENTITY_MAPPING_ID_IN_FILTER, entityMappingIds[0]);
                        super.openFilterFormWithField(entityDescriptor, field, position, this.filterFormRef);
                    }
                }
            }
        }

        onDoubleClickItem(entity: any) {
            //restrict edit mode
        }

        protected renderContextMenuItems(entity: any): React.ReactNode {
            return null;
        }

        protected preRenderButtons(params: any): Array<OverrideableElement> {
            return super.preRenderButtons(params).filter((x : any) => (!x.props || x.props.key !== "add") && (!x.element || !["fileImporter", "fileExporter"].includes(x.element.key)));
        }

        renderMain() {
            return (
                <>
                    {super.renderMain()}
                    <AuditedFieldDetails {...this.props.auditedFieldDetails} dispatchers={this.props.dispatchers.auditedFieldDetails} />
                    <AuditFieldsExtendedView content={this.props.extendedViewEntity} openFor={this.props.extendedViewForEntity} dispatchers={this.props.dispatchers} />
                </>
            );
        }
    };

    (auditEntityDescriptor.infoTable.slice as SliceEntityTablePage).setOptions({ countMode: undefined });
    auditEntityDescriptor.infoTable.mapBigStateToProps = (state: BigState, props: any) => {   
        props.itemsHidedFromCell = ["add"];
    }
});
